import { useState, useCallback, useRef, useEffect } from 'react';
import {
  Card,
  Button,
  Badge,
  LoadingSkeleton,
  useToast,
} from '@nexora/design-system';
import { apiClient } from '../../shared/api/client';
import { queryKeys } from '../../shared/api/query-keys';
import { useQueryClient } from '@tanstack/react-query';

/* ── Types ── */

export interface ImportRow {
  [key: string]: string;
}

export interface ImportPayload {
  entityType: string;
  rows: ImportRow[];
  strategy: 'error' | 'skip' | 'update';
}

export interface ImportResult {
  entityType: string;
  rowsProcessed: number;
  rowsSucceeded: number;
  rowsFailed: number;
  errors: { row: number; field: string; message: string }[];
}

export interface ImportJob {
  jobId: string;
}

export interface ImportStatus {
  status: 'processing' | 'completed' | 'failed';
  result?: ImportResult;
  error?: string;
}

/* ── Constants ── */

const ENTITY_TYPES = [
  { value: 'product', label: 'Produtos' },
  { value: 'party', label: 'Parceiros (Clientes/Fornecedores)' },
  { value: 'service', label: 'Serviços' },
  { value: 'category', label: 'Categorias' },
];

const STRATEGIES = [
  { value: 'error', label: 'Parar no primeiro erro' },
  { value: 'skip', label: 'Pular linhas com erro' },
  { value: 'update', label: 'Atualizar registros existentes' },
];

const COLUMN_MAP: Record<string, string[]> = {
  product: ['name', 'sku', 'gtin', 'ncm', 'unit', 'priceSale', 'priceCost', 'category', 'brand', 'notes'],
  party: ['name', 'legalName', 'documentType', 'documentNumber', 'email', 'phone', 'type', 'notes'],
  service: ['name', 'code', 'description', 'nbsCode', 'unit', 'price', 'notes'],
  category: ['name', 'code', 'type', 'parentCode', 'notes'],
};

/* ── Page ── */

export function MasterDataImportPage(): JSX.Element {
  const { addToast } = useToast();
  const qc = useQueryClient();

  const [entityType, setEntityType] = useState('product');
  const [strategy, setStrategy] = useState<string>('error');
  const [rows, setRows] = useState<ImportRow[]>([]);
  const [headers, setHeaders] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [jobId, setJobId] = useState<string | null>(null);
  const [jobStatus, setJobStatus] = useState<ImportStatus | null>(null);
  const [polling, setPolling] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Reset when entity type changes
  useEffect(() => {
    setRows([]);
    setHeaders([]);
    setJobId(null);
    setJobStatus(null);
  }, [entityType]);

  // Poll job status
  useEffect(() => {
    if (!jobId || jobStatus?.status === 'completed' || jobStatus?.status === 'failed') return;

    setPolling(true);
    const interval = setInterval(async () => {
      try {
        const status = await apiClient.get<ImportStatus>(`/import/${jobId}/status`);
        setJobStatus(status);
        if (status.status === 'completed' || status.status === 'failed') {
          clearInterval(interval);
          setPolling(false);
          qc.invalidateQueries({ queryKey: queryKeys.masterData.importJobs() });
        }
      } catch {
        clearInterval(interval);
        setPolling(false);
      }
    }, 2000);

    return () => {
      clearInterval(interval);
      setPolling(false);
    };
  }, [jobId, jobStatus?.status, qc]);

  const handleFileUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      const text = evt.target?.result as string;
      const lines = text.split('\n').filter((l) => l.trim());

      if (lines.length < 2) {
        addToast('Arquivo deve conter cabeçalho + pelo menos 1 linha', 'error');
        return;
      }

      const parsedHeaders = lines[0]!.split(',').map((h) => h.trim().replace(/^"/, '').replace(/"$/, ''));
      setHeaders(parsedHeaders);

      const parsedRows = lines.slice(1).map((line) => {
        const values = line.split(',').map((v) => v.trim().replace(/^"/, '').replace(/"$/, ''));
        const row: ImportRow = {};
        parsedHeaders.forEach((h, i) => {
          row[h] = values[i] ?? '';
        });
        return row;
      });

      setRows(parsedRows);
      addToast(`${parsedRows.length} linha(s) carregadas do arquivo.`, 'success');
    };
    reader.readAsText(file);

    // Reset input so re-uploading same file triggers change
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [addToast]);

  const handleSubmit = useCallback(async () => {
    if (rows.length === 0) return;

    setSubmitting(true);
    setJobId(null);
    setJobStatus(null);

    try {
      const payload: ImportPayload = { entityType, rows, strategy: strategy as ImportPayload['strategy'] };
      const result = await apiClient.post<ImportJob>('/import', payload);
      setJobId(result.jobId);
      addToast('Importação enviada. Processando...', 'success');
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Erro ao enviar importação', 'error');
    } finally {
      setSubmitting(false);
    }
  }, [entityType, rows, strategy, addToast]);

  const suggestedColumns = COLUMN_MAP[entityType] ?? [];
  const valid = rows.length > 0;
  const isDone = jobStatus?.status === 'completed' || jobStatus?.status === 'failed';

  return (
    <div className="import-page">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
        <div>
          <h1 className="font-display-lg text-display-lg text-on-surface m-0">Importação de Dados Mestre</h1>
          <p className="font-body-md text-body-md text-on-surface-variant mt-xs">Importação em lote de produtos e parceiros.</p>
        </div>
      </div>

      {/* Entity type and strategy selection */}
      <Card padding="1rem" variant="outlined" className="import-card">
        <div className="import-config">
          <div className="import-config__field">
            <label className="import-config__label">Tipo de Entidade</label>
            <select
              className="import-config__select"
              value={entityType}
              onChange={(e) => setEntityType(e.target.value)}
              disabled={submitting || polling}
            >
              {ENTITY_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>
          <div className="import-config__field">
            <label className="import-config__label">Estratégia</label>
            <select
              className="import-config__select"
              value={strategy}
              onChange={(e) => setStrategy(e.target.value)}
              disabled={submitting || polling}
            >
              {STRATEGIES.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="import-hint">
          <p className="import-hint__title">Colunas esperadas para {ENTITY_TYPES.find((t) => t.value === entityType)?.label}:</p>
          <code className="import-hint__cols">{suggestedColumns.join(', ')}</code>
        </div>
      </Card>

      {/* Upload area */}
      <Card padding="1rem" variant="outlined" className="import-card">
        <div className="import-upload">
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            style={{ display: 'none' }}
            onChange={handleFileUpload}
          />
          <Button
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={submitting || polling}
          >
            Upload CSV
          </Button>
          <span className="import-upload__hint">
            {rows.length > 0
              ? `${rows.length} linha(s) carregadas`
              : 'Clique para selecionar um arquivo CSV'}
          </span>
        </div>
      </Card>

      {/* Preview table */}
      {rows.length > 0 && (
        <Card padding="0" variant="outlined" className="import-card">
          <div className="import-preview-header">
            <h3>Pré-visualização ({rows.length} linhas)</h3>
            {headers.length > 0 && (
              <p className="import-preview-cols">{headers.join(' | ')}</p>
            )}
          </div>
          <div className="import-preview-table-wrap">
            <table className="import-preview-table">
              <thead>
                <tr>
                  <th>#</th>
                  {headers.map((h) => (
                    <th key={h}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.slice(0, 10).map((row, idx) => (
                  <tr key={idx}>
                    <td className="import-preview-row-num">{idx + 1}</td>
                    {headers.map((h) => (
                      <td key={h} title={row[h]}>{row[h]}</td>
                    ))}
                  </tr>
                ))}
                {rows.length > 10 && (
                  <tr>
                    <td colSpan={headers.length + 1} style={{ textAlign: 'center', color: '#74777f', padding: '0.75rem' }}>
                      ... e mais {rows.length - 10} linha(s)
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="import-submit">
            <Button
              variant="primary"
              onClick={handleSubmit}
              loading={submitting}
              disabled={!valid || submitting || polling}
            >
              {submitting ? 'Enviando...' : `Importar ${rows.length} linha(s)`}
            </Button>
          </div>
        </Card>
      )}

      {/* Job status */}
      {jobId && (
        <Card padding="1rem" variant="outlined">
          <h3 className="font-headline-sm text-headline-sm text-on-surface mt-0 mb-sm">Status da Importação</h3>

          {polling && !isDone && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', color: '#43474e' }}>
              <LoadingSkeleton />
              <span>Processando... ID: {jobId}</span>
            </div>
          )}

          {isDone && jobStatus && (
            <div>
              <div style={{ marginBottom: '0.75rem' }}>
                <Badge variant={jobStatus.status === 'completed' ? 'success' : 'error'} size="md">
                  {jobStatus.status === 'completed' ? 'Concluído' : 'Falhou'}
                </Badge>
              </div>

              {jobStatus.error && (
                <div className="import-job-error">
                  <p>Erro: {jobStatus.error}</p>
                </div>
              )}

              {jobStatus.result && (
                <div className="import-job-result">
                  <div className="import-job-stat">
                    <span className="import-job-stat__label">Processadas</span>
                    <span className="import-job-stat__value">{jobStatus.result.rowsProcessed}</span>
                  </div>
                  <div className="import-job-stat import-job-stat--success">
                    <span className="import-job-stat__label">Sucesso</span>
                    <span className="import-job-stat__value">{jobStatus.result.rowsSucceeded}</span>
                  </div>
                  <div className="import-job-stat import-job-stat--error">
                    <span className="import-job-stat__label">Falhas</span>
                    <span className="import-job-stat__value">{jobStatus.result.rowsFailed}</span>
                  </div>
                </div>
              )}

              {jobStatus.result?.errors && jobStatus.result.errors.length > 0 && (
                <div className="import-job-errors">
                  <h4 style={{ fontSize: '0.875rem', fontWeight: 600, margin: '0.75rem 0 0.5rem' }}>Erros por linha:</h4>
                  <div className="import-job-errors-table-wrap">
                    <table className="import-job-errors-table">
                      <thead>
                        <tr>
                          <th>Linha</th>
                          <th>Campo</th>
                          <th>Mensagem</th>
                        </tr>
                      </thead>
                      <tbody>
                        {jobStatus.result.errors.slice(0, 50).map((err, idx) => (
                          <tr key={idx}>
                            <td>{err.row}</td>
                            <td>{err.field}</td>
                            <td>{err.message}</td>
                          </tr>
                        ))}
                        {jobStatus.result.errors.length > 50 && (
                          <tr>
                            <td colSpan={3} style={{ textAlign: 'center', color: '#74777f', padding: '0.5rem' }}>
                              ... e mais {jobStatus.result.errors.length - 50} erro(s)
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </Card>
      )}

      <style>{`
        .import-page { padding: 1.5rem; max-width: 1000px; margin: 0 auto; }
        .import-card { margin-bottom: 1rem; }
        .import-config { display: flex; gap: 1.5rem; margin-bottom: 1rem; }
        .import-config__field { flex: 1; }
        .import-config__label { display: block; font-size: 12px; font-weight: 500; color: #43474e; margin-bottom: 0.25rem; font-family: 'JetBrains Mono', ui-monospace, monospace; letter-spacing: 0.02em;}
        .import-config__select { width: 100%; padding: 0.5rem; border: 1px solid #c4c6cf; border-radius: 4px; font-size: 0.875rem; height: 40px; }
        .import-hint { padding: 0.75rem; background: #f2f4f6; border-radius: 4px; }
        .import-hint__title { margin: 0 0 0.25rem; font-size: 0.8125rem; color: #43474e; }
        .import-hint__cols { font-size: 0.75rem; color: #002045; }
        .import-upload { display: flex; align-items: center; gap: 1rem; }
        .import-upload__hint { font-size: 0.875rem; color: #74777f; }
        .import-preview-header { padding: 0.75rem 1rem; border-bottom: 1px solid #e0e3e5; }
        .import-preview-header h3 { margin: 0; font-size: 0.9375rem; font-weight: 600; }
        .import-preview-cols { margin: 0.25rem 0 0; font-size: 0.75rem; color: #74777f; font-family: monospace; }
        .import-preview-table-wrap { overflow-x: auto; }
        .import-preview-table { width: 100%; border-collapse: collapse; font-size: 0.8125rem; }
        .import-preview-table th { text-align: left; padding: 0.5rem 0.75rem; background: #f2f4f6; color: #43474e; font-weight: 600; font-size: 0.75rem; border-bottom: 2px solid #e0e3e5; white-space: nowrap; position: sticky; top: 0; z-index: 1;}
        .import-preview-table td { padding: 0.375rem 0.75rem; border-bottom: 1px solid #eceef0; max-width: 200px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .import-preview-row-num { color: #74777f; font-weight: 500; }
        .import-submit { padding: 0.75rem 1rem; border-top: 1px solid #e0e3e5; display: flex; justify-content: flex-end; }
        .import-job-error { padding: 0.75rem; background: #ffdad6; border-radius: 4px; color: #ba1a1a; font-size: 0.875rem; margin-bottom: 0.75rem; }
        .import-job-result { display: flex; gap: 1rem; margin-bottom: 0.75rem; }
        .import-job-stat { flex: 1; padding: 0.75rem; background: #f2f4f6; border-radius: 4px; text-align: center; }
        .import-job-stat--success { background: #d3f5d6; }
        .import-job-stat--error { background: #ffdad6; }
        .import-job-stat__label { display: block; font-size: 12px; color: #43474e; margin-bottom: 0.25rem; font-family: 'JetBrains Mono', ui-monospace, monospace; font-weight: 500; letter-spacing: 0.02em;}
        .import-job-stat__value { font-size: 1.5rem; font-weight: 700; color: #191c1e; }
        .import-job-errors-table-wrap { overflow-x: auto; }
        .import-job-errors-table { width: 100%; border-collapse: collapse; font-size: 0.8125rem; }
        .import-job-errors-table th { text-align: left; padding: 0.5rem 0.75rem; background: #f2f4f6; color: #43474e; font-weight: 600; font-size: 0.75rem; border-bottom: 2px solid #e0e3e5; position: sticky; top: 0; z-index: 1;}
        .import-job-errors-table td { padding: 0.375rem 0.75rem; border-bottom: 1px solid #eceef0; }
      `}</style>
    </div>
  );
}
